import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Printer, Mail, Check, Phone, Loader2 } from "lucide-react";

const PLANS = [
  { key: "essential", name: "Essential", price: "$179", mo: "$14.95/mo" },
  { key: "advantage", name: "Advantage", price: "$299", mo: "$25/mo" },
  { key: "elite", name: "Elite", price: "$499", mo: "$42/mo", featured: true },
];
const C = <Check className="h-4 w-4 text-green-600 mx-auto" />;
const X = <span className="text-slate-300">—</span>;
const ROWS = [
  ["2 Precision Tune-Ups (Spring & Fall)", C, C, C],
  ["3rd Visit — Mid-Summer Drain Flush", X, X, C],
  ["Priority Scheduling", C, C, C],
  ["No After-Hours Fees", C, C, C],
  ["Repair Discount", "15% OFF", "20% OFF", "30% OFF"],
  ["Free 1 lb Refrigerant / Year*", X, C, C],
  ["$250 System Replacement Credit", X, C, C],
  ["Waived Service Call Fee", "With Repair", "Always", "Always"],
  ["Lifetime Repair Warranty (workmanship)", X, X, C],
];

export default function ServiceAgreement() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  // Online enrollment (Square)
  const [ePlan, setEPlan] = useState("elite");
  const [eInterval, setEInterval] = useState("monthly");
  const [eName, setEName] = useState("");
  const [eEmail, setEEmail] = useState("");
  const [ePhone, setEPhone] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  const enrollNow = async () => {
    if (!eName.trim()) { toast.error("Enter your name."); return; }
    if (!eEmail.includes("@")) { toast.error("Enter a valid email."); return; }
    setEnrolling(true);
    try {
      const { data } = await api.post("/public/comfort-club/checkout", {
        plan: ePlan, interval: eInterval, name: eName, email: eEmail, phone: ePhone,
        origin_url: window.location.origin,
      });
      if (data.url) window.location.assign(data.url);
      else toast.error("Could not start checkout. Please call 321-292-1189.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please call 321-292-1189.");
    } finally { setEnrolling(false); }
  };

  const sendEmail = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email."); return; }
    setSending(true);
    try {
      const r = await api.post("/service-agreement/email", { email, name });
      if (r.data.ok) toast.success(`Sent to ${email}`);
      else toast.error("Email service unavailable right now.");
    } catch { toast.error("Could not send. Try again."); }
    finally { setSending(false); }
  };

  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent("https://aquilaairandheating.com/service-agreement")}`;

  return (
    <>
      <Seo title="Aquila Comfort Club — Maintenance Membership | Aquila Air & Heating" description="Choose your Aquila Air & Heating maintenance membership: Essential, Advantage, or Elite. Precision tune-ups, priority scheduling, repair discounts and more for Brevard County homes." path="/service-agreement/" />
      <style>{`@media print { header, footer, .no-print { display: none !important; } .print-tight { padding: 0 !important; } }`}</style>

      <section className="bg-slate-50 py-10 print-tight" data-testid="service-agreement-page">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-launch">Aquila Comfort Club</p>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-navy mt-1">Maintenance Membership</h1>
                <p className="text-slate-500 text-sm mt-1">Circle the plan you chose. Leave-behind service agreement.</p>
              </div>
              <img src={qr} alt="Scan to enroll online" width="90" height="90" className="rounded border border-slate-200" />
            </div>

            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2"><span className="font-semibold text-navy">Customer:</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="sa-customer-name" className="border-b border-slate-300 focus:outline-none focus:border-launch px-1 py-0.5 min-w-[180px]" />
              </label>
            </div>

            <div className="mt-5 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="text-left p-2 text-slate-500 font-semibold w-1/3">Plan</th>
                    {PLANS.map((p) => (
                      <th key={p.key} data-testid={`plan-${p.key}`} className={`p-2 text-center align-top ${p.featured ? "bg-launch/10 rounded-t-lg" : ""}`}>
                        <div className="font-extrabold text-navy">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.price}</div>
                        <div className="text-xs font-semibold text-launch">{p.mo}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2 text-slate-700">{r[0]}</td>
                      <td className="p-2 text-center">{r[1]}</td>
                      <td className="p-2 text-center">{r[2]}</td>
                      <td className="p-2 text-center bg-launch/5">{r[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2">*If needed — we check refrigerant levels every visit.</p>

            <div className="grid sm:grid-cols-2 gap-6 mt-6 text-sm">
              <div><span className="font-semibold text-navy">Tech Notes:</span><div className="border-b border-slate-300 h-8 mt-1" /></div>
              <div><span className="font-semibold text-navy">Customer Signature:</span><div className="border-b border-slate-300 h-8 mt-1" /></div>
            </div>

            <div className="mt-6 bg-navy text-white rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
              <p className="font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-launch" /> Call <a href="tel:3212921189" className="underline">321-292-1189</a> to enroll or scan the QR.</p>
              <span className="text-slate-300 text-sm">aquilaairandheating.com/service-agreement</span>
            </div>
          </div>

          <div className="no-print mt-5 bg-white border border-slate-200 rounded-xl p-5 sm:p-6" data-testid="cc-enroll-panel">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-launch">Enroll Online</p>
            <h2 className="text-xl font-bold text-navy mt-1">Join the Comfort Club — Pay Securely</h2>
            <p className="text-sm text-slate-500 mt-1">Pick your plan and billing, then check out securely with Square. Cancel anytime.</p>

            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              {PLANS.map((p) => (
                <button key={p.key} onClick={() => setEPlan(p.key)} data-testid={`cc-plan-${p.key}`}
                  className={`text-left rounded-xl border p-4 transition-colors ${ePlan === p.key ? "border-launch ring-2 ring-launch/30 bg-launch/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-navy">{p.name}</span>
                    {ePlan === p.key && <Check className="h-4 w-4 text-launch" />}
                  </div>
                  <div className="text-sm font-semibold text-launch mt-1">{eInterval === "monthly" ? p.mo : `${p.price}/yr`}</div>
                </button>
              ))}
            </div>

            <div className="mt-4 inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50" role="tablist">
              {[["monthly", "Monthly"], ["annual", "Annual"]].map(([k, label]) => (
                <button key={k} onClick={() => setEInterval(k)} data-testid={`cc-interval-${k}`}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${eInterval === k ? "bg-navy text-white" : "text-slate-600"}`}>{label}</button>
              ))}
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              <input value={eName} onChange={(e) => setEName(e.target.value)} placeholder="Full name" data-testid="cc-name" className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-launch/40" />
              <input type="email" value={eEmail} onChange={(e) => setEEmail(e.target.value)} placeholder="Email" data-testid="cc-email" className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-launch/40" />
              <input value={ePhone} onChange={(e) => setEPhone(e.target.value)} placeholder="Phone (optional)" data-testid="cc-phone" className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-launch/40" />
            </div>

            <button onClick={enrollNow} disabled={enrolling} data-testid="cc-enroll-btn" className="mt-4 inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold px-6 py-2.5 rounded-lg disabled:opacity-60">
              {enrolling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enroll &amp; Pay Securely
            </button>
            <p className="text-xs text-slate-400 mt-2">Secure checkout powered by Square. You'll enter payment details on Square's hosted page.</p>
          </div>

          <div className="no-print mt-5 bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="font-bold text-navy mb-3">Send or print this agreement</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@email.com" data-testid="sa-email-input" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-launch/40" />
              <button onClick={sendEmail} disabled={sending} data-testid="sa-email-btn" className="inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold px-5 py-2 rounded-lg disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email to Customer
              </button>
              <button onClick={() => window.print()} data-testid="sa-print-btn" className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-semibold px-5 py-2 rounded-lg">
                <Printer className="h-4 w-4" /> Print
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
